import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type MealOption = { id: string; labelEn: string; labelFr: string; label?: string };
type Meal = { id: string; nameEn: string; nameFr: string; name?: string; options: MealOption[]; order: number };
type Registration = { email: string; name: string; choices: Record<string, string>; submittedAt: string };

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `Error ${res.status}`;
    try { const j = await res.json(); msg = j.error || msg; } catch { /* noop */ }
    throw new Error(msg);
  }
  return res.json();
}

export default function AdminMeals() {
  const [tab, setTab] = useState<'meals' | 'registrations'>('meals');
  const [meals, setMeals] = useState<Meal[]>([]);
  const [regs, setRegs] = useState<Registration[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // add-meal form state
  const [showAdd, setShowAdd] = useState(false);
  const [newEn, setNewEn] = useState('');
  const [newFr, setNewFr] = useState('');

  // add-option state (per meal)
  const [optFor, setOptFor] = useState<string | null>(null);
  const [optEn, setOptEn] = useState('');
  const [optFr, setOptFr] = useState('');

  const loadMeals = async () => { try { const d = await handle<{ meals: Meal[] }>(await fetch('/api/meals-config')); setMeals(d.meals); } catch (e) { setError((e as Error).message); } };
  const loadRegs = async () => { try { const d = await handle<{ items: Registration[] }>(await fetch('/api/meals-registrations')); setRegs(d.items); } catch (e) { setError((e as Error).message); } };
  useEffect(() => { loadMeals(); loadRegs(); }, []);

  const saveMeal = async (payload: Partial<Meal> & { id?: string }) => {
    setBusy(true); setError(null);
    try {
      await handle(await fetch('/api/meals-config-save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }));
      await loadMeals();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addMeal = async () => {
    if (!newEn.trim() || !newFr.trim()) { setError('Both English and French meal names are required.'); return; }
    await saveMeal({ nameEn: newEn.trim(), nameFr: newFr.trim(), options: [], order: meals.length });
    setNewEn(''); setNewFr(''); setShowAdd(false);
  };

  const deleteMeal = async (id: string) => {
    if (!confirm('Delete this meal and all its options?')) return;
    setBusy(true);
    try { await handle(await fetch('/api/meals-config-delete', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) })); await loadMeals(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addOption = async (m: Meal) => {
    if (!optEn.trim() || !optFr.trim()) { setError('Both English and French option labels are required.'); return; }
    const opt: MealOption = { id: 'opt-' + Date.now(), labelEn: optEn.trim(), labelFr: optFr.trim() };
    await saveMeal({ id: m.id, nameEn: m.nameEn, nameFr: m.nameFr, options: [...m.options, opt], order: m.order });
    setOptEn(''); setOptFr(''); setOptFor(null);
  };

  const removeOption = async (m: Meal, optId: string) => {
    await saveMeal({ id: m.id, nameEn: m.nameEn, nameFr: m.nameFr, options: m.options.filter((o) => o.id !== optId), order: m.order });
  };

  const clearReg = async (email: string) => {
    if (!confirm(`Clear ${email}'s meal selection? They'll be able to register again.`)) return;
    setBusy(true);
    try { await handle(await fetch('/api/meals-registrations', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, choices: null }) })); await loadRegs(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const headers = ['Name', 'Email', ...meals.map((m) => m.nameEn), 'SubmittedAt'];
    const rows = regs.map((r) => {
      const cells = [r.name, r.email];
      for (const m of meals) { const o = m.options.find((x) => x.id === r.choices[m.id]); cells.push(o?.labelEn || r.choices[m.id] || ''); }
      cells.push(r.submittedAt || '');
      return cells.map((c) => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `meal-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const counts: Record<string, Record<string, number>> = {};
  for (const m of meals) { counts[m.id] = {}; for (const o of m.options) counts[m.id][o.id] = 0; }
  for (const r of regs) for (const m of meals) { const c = r.choices[m.id]; if (c && counts[m.id]?.[c] !== undefined) counts[m.id][c]++; }

  const inputCls = 'flex-1 bg-bg-base border border-white/[0.12] rounded-lg px-3 py-2 text-ink text-sm outline-none focus:border-brand-red';

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 pt-36 pb-32">
      <Link to="/admin" className="text-xs text-ink-muted hover:text-ink lowercase">← back to admin</Link>
      <p className="text-xs tracking-[0.25em] uppercase text-brand-red mt-5 mb-3 font-mono">Meals</p>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight text-ink">Meal management.</h1>

      <div className="flex gap-2 mt-8 mb-8 border-b border-white/[0.07] pb-4">
        {(['meals', 'registrations'] as const).map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} className={`px-4 py-2 rounded-full text-sm capitalize transition-colors ${tab === tb ? 'bg-brand-red text-white' : 'bg-bg-elev text-ink-muted hover:text-ink border border-white/[0.08]'}`}>{tb}</button>
        ))}
      </div>

      {error && <div className="mb-6 p-4 rounded-xl bg-red-950/50 border border-red-700/50 text-red-300 text-sm">{error}</div>}

      {tab === 'meals' && (
        <section>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowAdd((v) => !v)} disabled={busy} className="bg-brand-red text-white text-sm rounded-full px-5 py-2 hover:bg-brand-red-soft transition-colors">+ Add meal</button>
          </div>

          {showAdd && (
            <div className="card p-6 mb-4">
              <p className="text-sm text-ink-muted mb-3">New meal — name in both languages:</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input className={inputCls} placeholder="Name (English) e.g. Lunch" value={newEn} onChange={(e) => setNewEn(e.target.value)} />
                <input className={inputCls} placeholder="Nom (Français) p. ex. Dîner" value={newFr} onChange={(e) => setNewFr(e.target.value)} />
                <button onClick={addMeal} disabled={busy} className="bg-brand-red text-white text-sm rounded-lg px-5 py-2 hover:bg-brand-red-soft">Save</button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {meals.map((m) => (
              <div key={m.id} className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-display text-2xl text-ink">{m.nameEn}</p>
                    <p className="text-sm text-ink-muted">{m.nameFr}</p>
                  </div>
                  <button onClick={() => deleteMeal(m.id)} className="text-xs px-3 py-1.5 rounded-full border border-white/[0.15] text-ink hover:border-brand-red hover:text-brand-red">Delete</button>
                </div>
                <div className="space-y-2">
                  {m.options.map((o) => (
                    <div key={o.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-bg-base border border-white/[0.06]">
                      <span className="text-ink text-sm">{o.labelEn} <span className="text-ink-faint">/ {o.labelFr}</span></span>
                      <button onClick={() => removeOption(m, o.id)} className="text-ink-faint hover:text-brand-red text-sm">remove</button>
                    </div>
                  ))}
                  {m.options.length === 0 && <p className="text-ink-faint text-sm">No options yet.</p>}
                </div>

                {optFor === m.id ? (
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <input className={inputCls} placeholder="Option (English)" value={optEn} onChange={(e) => setOptEn(e.target.value)} />
                    <input className={inputCls} placeholder="Option (Français)" value={optFr} onChange={(e) => setOptFr(e.target.value)} />
                    <button onClick={() => addOption(m)} disabled={busy} className="bg-brand-red text-white text-sm rounded-lg px-4 py-2 hover:bg-brand-red-soft">Add</button>
                    <button onClick={() => { setOptFor(null); setOptEn(''); setOptFr(''); }} className="text-sm text-ink-muted px-3">cancel</button>
                  </div>
                ) : (
                  <button onClick={() => { setOptFor(m.id); setOptEn(''); setOptFr(''); }} className="mt-4 text-sm text-brand-red hover:underline">+ add option</button>
                )}
              </div>
            ))}
            {meals.length === 0 && <p className="text-ink-muted">No meals configured yet. Click "+ Add meal" to start.</p>}
          </div>
        </section>
      )}

      {tab === 'registrations' && (
        <section>
          <div className="flex justify-between items-center mb-6">
            <p className="text-ink-muted text-sm">{regs.length} registration{regs.length === 1 ? '' : 's'}</p>
            <button onClick={exportCsv} className="bg-brand-red text-white text-sm rounded-full px-5 py-2 hover:bg-brand-red-soft">Export CSV</button>
          </div>

          {meals.length > 0 && regs.length > 0 && (
            <div className="card p-6 mb-6">
              <p className="text-xs uppercase tracking-wider text-ink-muted mb-4 font-mono">Catering counts</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {meals.map((m) => (
                  <div key={m.id}>
                    <p className="text-ink font-medium mb-2">{m.nameEn}</p>
                    {m.options.map((o) => (
                      <div key={o.id} className="flex justify-between text-sm py-1">
                        <span className="text-ink-muted">{o.labelEn}</span>
                        <span className="text-ink font-mono">{counts[m.id]?.[o.id] || 0}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-muted border-b border-white/[0.06]">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  {meals.map((m) => <th key={m.id} className="px-4 py-3 font-medium">{m.nameEn}</th>)}
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {regs.map((r) => (
                  <tr key={r.email} className="border-b border-white/[0.04] last:border-0">
                    <td className="px-4 py-3 text-white">{r.name}</td>
                    <td className="px-4 py-3 text-ink-muted">{r.email}</td>
                    {meals.map((m) => (
                      <td key={m.id} className="px-4 py-3 text-ink">{m.options.find((o) => o.id === r.choices[m.id])?.labelEn || r.choices[m.id] || '—'}</td>
                    ))}
                    <td className="px-4 py-3"><button onClick={() => clearReg(r.email)} className="text-xs text-ink-faint hover:text-brand-red">clear</button></td>
                  </tr>
                ))}
                {regs.length === 0 && <tr><td colSpan={meals.length + 3} className="px-4 py-6 text-ink-muted text-center">No registrations yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
